/**
 * AvailabilityBoard: the Team > Availability table, one row per billable
 * staff member, care managers and SPICE therapists together, grouped by
 * unit (SPICE always as its own trailing group, whatever a therapist's
 * own unit_name says). Every row reads through the same cells: person,
 * target, last week, this week so far, active clients, the "New clients"
 * stance, and a quiet-chip notes cell. A SPICE therapist's row reads in
 * hours exactly as a care manager's does: her hours are the notes she
 * posted (a session is an hour, a Friendly Visitor visit carries its
 * own), and her target is the same target_weekly_hours a care manager's
 * is (weeklyTargetHours). Session plans, outcomes and declines belong to
 * the SPICE schedule grid, not this table.
 *
 * The unit is named once, on the group header beside its counties; a
 * row carries no unit cell of its own.
 *
 * The stance is the shared SegmentedToggle, Open, Talk to Me and Not Open,
 * only for canEdit (manage_staff_availability) holders, and a read-only
 * stance chip otherwise. It is compact (each segment its label plus the
 * compact padding) and in the house looks: Open checked is the primary
 * look, Talk to Me the gold, Not Open the faded terracotta, with no ring
 * and no pill; the others stay transparent on the sand track, with no dot.
 * On leave in Notes is the colourless grey chip. Its tooltip names its
 * two-week reach (a save sets the selected week and, unless it was set
 * apart, the following one). While a row saves, its segments are
 * aria-disabled, never disabled, so the keyboard focus stays where it is,
 * and the pending stance shows checked. The rarer per-week fields (on
 * leave, a note) live behind the "More" action, which opens
 * AvailabilityDialog for a care manager and a SPICE therapist alike;
 * canEdit gates that too. Targets themselves are set on the People page.
 *
 * The toggle, the chip and Notes show the stored override the server
 * sent on each row (availability; with none recorded, the stance the
 * server sent for the week). The "New clients" and Notes columns render
 * only when some row carries it; a row without it gets empty cells, no
 * toggle and no More, so no write can start from it. When the columns do
 * not fit, the table scrolls inside its own card.
 *
 * THE WEEKLY CALL (LP-9). Notes holds the office's weekly call note for
 * that person as a block (CallNote: wrapped, line breaks kept, four lines
 * with its own scroll, Show all to twelve), and under it the clients
 * named on that call (RowMentions: chips on at most two lines, then "+N
 * more", which opens the dialog). Above the people rows, one compact band
 * (HospiceDeathsBand) shows the company's clients on hospice and those who
 * died in the last eight weeks, read from the client record. The four
 * numeric columns (Target, Last week, This week, Clients) share one
 * narrow width, right-aligned, no header wraps, and Notes takes the
 * rest: the widest column.
 */

import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { CmPhoto } from '@/components/workspace/CmPhoto';
import { TextAction } from '@/components/workspace/TextAction';
import { Chip } from '@/components/primitives/Chip';
import { SegmentedToggle, type SegmentedOption } from '@/components/primitives/SegmentedToggle';
import { UnitGroupHeaderRow } from '@/components/team/RosterTable';
import { StanceChip } from '@/components/capacity/StanceChip';
import { CallNote } from '@/components/capacity/CallNote';
import { RowMentions } from '@/components/capacity/RowMentions';
import { HospiceDeathsBand } from '@/components/capacity/HospiceDeathsBand';
import {
  STATED_AVAILABILITY_LABEL,
  formatHours,
  groupByUnit,
  storedStance,
  weeklyTargetHours,
  type NewClientStance,
} from '@/lib/capacity';
import type { AvailabilityRowWire, CallMentionWire, HospiceAndDeathsWire } from '@/lib/capacityApi';

export interface StanceError {
  rosterId: string;
  message: string;
}

/** The row whose stance is saving, and the stance it is saving. */
export interface PendingStance {
  rosterId: string;
  stance: NewClientStance;
}

export interface AvailabilityBoardProps {
  tenantSlug: string;
  rows: AvailabilityRowWire[];
  /** Whether the stance toggle is interactive and the "More" action renders (manage_staff_availability). */
  canEdit: boolean;
  /** Whether a care manager's name links to the care-manager view (view_all_clinical). */
  linkToCareManager: boolean;
  onMore: (row: AvailabilityRowWire) => void;
  onSetNewClientStance: (row: AvailabilityRowWire, stance: NewClientStance) => void;
  /** The row currently saving a stance, or null. */
  pending: PendingStance | null;
  stanceError: StanceError | null;
  /** The clients named on this week's calls, by roster id. */
  mentionsByRoster?: ReadonlyMap<string, CallMentionWire[]>;
  /** The company's clients on hospice and recently died, from the record;
   *  null while unread, and then no band. */
  hospiceAndDeaths?: HospiceAndDeathsWire | null;
  /** The practice day, 'YYYY-MM-DD', for the band's dates. */
  today?: string;
}

/** One narrow width for the four numeric columns: the longest header
 *  ("Last week") on one line with the cell's padding, and no more, so
 *  Notes keeps the rest of the row. Written out whole so Tailwind finds
 *  it. */
const NUMERIC_COL = 'w-[5.5rem]';

/** A numeric cell: right-aligned figures, its first line level with the
 *  name beside the photo. */
const NUMERIC_CELL = 'px-1.5 pt-[0.9375rem] pb-3 text-right tabular-nums';

/** Person, target, last week, this week so far, active clients. */
const FACT_COLUMN_COUNT = 5;
/** New clients and Notes, shown when some row carries its override. */
const STANCE_COLUMN_COUNT = 2;

function NotesCell({
  row,
  mentions,
  onMore,
}: {
  row: AvailabilityRowWire;
  mentions: CallMentionWire[];
  onMore: (() => void) | null;
}): ReactNode {
  const a = row.availability;
  return (
    <div data-testid="availability-notes" className="min-w-0">
      {a?.on_leave && (
        <span className="mb-1 flex">
          <Chip variant="on-leave">On leave</Chip>
        </span>
      )}
      {a?.notes && <CallNote note={a.notes} rosterId={row.roster_id} />}
      <RowMentions key={mentions.map((m) => `${m.client_id}:${m.tag}`).join('|')} rosterId={row.roster_id} mentions={mentions} onMore={onMore} />
    </div>
  );
}

/** The switch's three options, in the board's order: each stance's
 *  label, test id suffix and look (Open the primary look, Talk to Me the
 *  gold, Not Open the faded terracotta). */
const STANCE_SEGMENTS = [
  { value: 'open', label: STATED_AVAILABILITY_LABEL.open, suffix: 'open' },
  { value: 'talk_to_me', label: STATED_AVAILABILITY_LABEL.talk_to_me, suffix: 'talk-to-me', checkedLook: 'gold' },
  { value: 'not_open', label: STATED_AVAILABILITY_LABEL.not_open, suffix: 'not-open', checkedLook: 'terracotta' },
] as const satisfies ReadonlyArray<SegmentedOption<NewClientStance>>;

function NewClientStanceCell({
  row,
  stored,
  canEdit,
  pending,
  error,
  onSet,
}: {
  row: AvailabilityRowWire;
  stored: NewClientStance;
  canEdit: boolean;
  /** The stance this row is saving, or null. */
  pending: NewClientStance | null;
  error: string | null;
  onSet: (row: AvailabilityRowWire, stance: NewClientStance) => void;
}): ReactNode {
  const testId = `availability-new-clients-${row.roster_id}`;
  // Someone else has this week open (0216): the toggle is off for this row
  // until they close it; the line under the name says who.
  const heldByOther = row.edit_lease != null && !row.edit_lease.mine;
  if (!canEdit || heldByOther) {
    return <StanceChip stance={stored} data-testid={testId} />;
  }

  const saving = pending !== null;
  const checked = pending ?? stored;

  const choose = (stance: NewClientStance): void => {
    if (saving || stance === checked) return;
    onSet(row, stance);
  };

  return (
    <>
        <SegmentedToggle<NewClientStance>
          options={STANCE_SEGMENTS}
          value={checked}
          onChange={choose}
          name={`New clients: ${row.full_name}`}
          title="Applies to this week and next"
          busy={saving}
          dense
          testIdPrefix={testId}
        />
        {error && (
          <p role="alert" className="mt-1 text-xs text-danger">{error}</p>
        )}
    </>
  );
}

interface RowViewProps {
  tenantSlug: string;
  row: AvailabilityRowWire;
  canEdit: boolean;
  linkToCareManager: boolean;
  onMore: (row: AvailabilityRowWire) => void;
  onSetNewClientStance: (row: AvailabilityRowWire, stance: NewClientStance) => void;
  pending: PendingStance | null;
  stanceError: StanceError | null;
  /** Whether the stance and Notes columns render (some row carries its override). */
  showStance: boolean;
  mentions: CallMentionWire[];
}

function RowView({
  tenantSlug,
  row,
  canEdit,
  linkToCareManager,
  onMore,
  onSetNewClientStance,
  pending,
  stanceError,
  showStance,
  mentions,
}: RowViewProps): ReactNode {
  const target = weeklyTargetHours(row);
  const sent = row.availability !== undefined;
  const stored = storedStance(row);

  return (
    <tr data-testid={`availability-row-${row.roster_id}`} data-roster-id={row.roster_id} className="border-t border-sage-800/[8%] align-top">
      <td className="px-4 py-3">
        <div className="flex items-center gap-2.5">
          <CmPhoto tenantSlug={tenantSlug} rosterId={row.roster_id} name={row.full_name} size="sm" hasPhoto={row.has_photo} />
          {row.kind === 'care_manager' && linkToCareManager ? (
            <Link
              to={`/t/${tenantSlug}/care-managers/${row.roster_id}`}
              className="font-semibold text-sage-800 no-underline hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
              data-testid="availability-name"
            >
              {row.full_name}
            </Link>
          ) : (
            <span className="font-semibold text-sage-800" data-testid="availability-name">{row.full_name}</span>
          )}
        </div>
        {row.edit_lease != null && !row.edit_lease.mine && (
          <p className="mt-1 text-xs text-ink-soft" data-testid={`availability-row-lease-${row.roster_id}`}>
            {`${row.edit_lease.holder_name} has this week open.`}
          </p>
        )}
      </td>
      <td className={`${NUMERIC_CELL}`} data-testid="availability-target">
        {target === null ? <span className="text-ink-soft">Not set</span> : `${formatHours(target)} h`}
      </td>
      <td className={NUMERIC_CELL} data-testid="availability-last-week">
        {formatHours(row.hours_worked_last_week) || '0'} h
      </td>
      <td className={NUMERIC_CELL} data-testid="availability-this-week">
        {formatHours(row.hours_worked) || '0'} h
      </td>
      <td className={NUMERIC_CELL} data-testid="availability-clients">{row.active_client_count}</td>
      {showStance && (
        <>
          <td className="px-3 py-3">
            {sent && stored !== undefined && (
              <NewClientStanceCell
                row={row}
                stored={stored}
                canEdit={canEdit}
                pending={pending?.rosterId === row.roster_id ? pending.stance : null}
                error={stanceError?.rosterId === row.roster_id ? stanceError.message : null}
                onSet={onSetNewClientStance}
              />
            )}
          </td>
          <td className="px-3 py-3">
            {sent && (
              <NotesCell row={row} mentions={mentions} onMore={canEdit ? () => onMore(row) : null} />
            )}
          </td>
          {canEdit && (
            <td className="px-2 py-3 text-right">
              {sent && (
                <TextAction data-testid={`availability-more-${row.roster_id}`} onClick={() => onMore(row)}>More</TextAction>
              )}
            </td>
          )}
        </>
      )}
    </tr>
  );
}

export function AvailabilityBoard({
  tenantSlug,
  rows,
  canEdit,
  linkToCareManager,
  onMore,
  onSetNewClientStance,
  pending,
  stanceError,
  mentionsByRoster,
  hospiceAndDeaths = null,
  today = '',
}: AvailabilityBoardProps): ReactNode {
  if (rows.length === 0) {
    return (
      <p className="px-4 py-4 text-sm text-ink-soft" data-testid="availability-empty">
        Mark who does care management or SPICE on the People page and you'll see them here.
      </p>
    );
  }

  const cmRows = rows.filter((r) => r.kind === 'care_manager');
  const spiceRows = rows.filter((r) => r.kind === 'spice_therapist');
  const cmGroups = groupByUnit(cmRows);
  // SPICE renders as its own trailing group regardless of any row's own
  // unit_name — the founder's ask, not an accident of unit-sorting.
  const groups = spiceRows.length > 0 ? [...cmGroups, { label: 'SPICE', unitName: null, rows: spiceRows }] : cmGroups;
  const showStance = rows.some((r) => r.availability !== undefined);
  const colSpan = FACT_COLUMN_COUNT + (showStance ? STANCE_COLUMN_COUNT + (canEdit ? 1 : 0) : 0);

  return (
    <>
    {/* The company band is its own card above the table, so the record's
        state never reads as part of the column headers. */}
    {hospiceAndDeaths !== null && (
      <div className="mb-3 bg-paper rounded-card shadow-sh2">
        <HospiceDeathsBand tenantSlug={tenantSlug} data={hospiceAndDeaths} today={today} />
      </div>
    )}
    <div className="relative overflow-x-auto bg-paper rounded-card shadow-sh2" data-testid="availability-board">
      <table className={`w-full table-fixed text-sm ${showStance ? 'min-w-[66rem]' : 'min-w-[44rem]'}`}>
        <thead>
          <tr className="text-left text-label uppercase tracking-[0.12em] text-sage-400 whitespace-nowrap">
            <th scope="col" className={`px-4 py-2.5 font-semibold ${showStance ? 'w-[13rem]' : ''}`}>Person</th>
            <th scope="col" className={`${NUMERIC_COL} px-1.5 py-2.5 font-semibold text-right`} data-testid="availability-col-numeric">Target</th>
            <th scope="col" className={`${NUMERIC_COL} px-1.5 py-2.5 font-semibold text-right`} data-testid="availability-col-numeric">Last week</th>
            <th scope="col" className={`${NUMERIC_COL} px-1.5 py-2.5 font-semibold text-right`} data-testid="availability-col-numeric">This week</th>
            <th scope="col" className={`${NUMERIC_COL} px-1.5 py-2.5 font-semibold text-right`} data-testid="availability-col-numeric">Clients</th>
            {showStance && (
              <>
                <th scope="col" className="w-[13.25rem] px-3 py-2.5 font-semibold">New clients</th>
                <th scope="col" className="px-3 py-2.5 font-semibold" data-testid="availability-col-notes">Notes</th>
                {canEdit && <th scope="col" className="w-[3.5rem] px-2 py-2.5"><span className="sr-only">More</span></th>}
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => (
            <Fragment key={g.label}>
              <UnitGroupHeaderRow
                label={g.label}
                counties={g.rows[0]?.unit_counties ?? []}
                hint={g.unitName === null && g.label !== 'SPICE' ? 'Set the unit on the People page.' : undefined}
                colSpan={colSpan}
                testIdPrefix="availability"
              />
              {g.rows.map((row) => (
                <RowView
                  key={row.roster_id}
                  tenantSlug={tenantSlug}
                  row={row}
                  canEdit={canEdit}
                  linkToCareManager={linkToCareManager}
                  onMore={onMore}
                  onSetNewClientStance={onSetNewClientStance}
                  pending={pending}
                  stanceError={stanceError}
                  showStance={showStance}
                  mentions={mentionsByRoster?.get(row.roster_id) ?? []}
                />
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
    </>
  );
}
